import { YMaps, Map, Placemark } from "@pbe/react-yandex-maps";
import { motion, type Variants } from "framer-motion";
import { useEffect, type FC } from "react";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const overlayVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.22 } },
  exit: { opacity: 0, transition: { duration: 0.18, delay: 0.05 } },
};

const sheetVariants: Variants = {
  hidden: { y: 72, opacity: 0, scale: 0.97 },
  visible: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: { type: "spring", stiffness: 300, damping: 30 },
  },
  exit: {
    y: 48,
    opacity: 0,
    scale: 0.97,
    transition: { duration: 0.18, ease: "easeIn" },
  },
};

export const Modal: FC<ModalProps> = ({ onClose }) => {
  const defaultState = {
    center: [53.979, 30.37139],
    zoom: 15,
    behaviors: ["default"],
  };

  useEffect(() => {
    const bodyOverflow = document.body.style.overflow;
    const htmlOverflow = document.documentElement.style.overflow;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    window.scrollTo(0, 0);
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = bodyOverflow;
      document.documentElement.style.overflow = htmlOverflow;
      window.scrollTo(scrollX, scrollY);
    };
  }, []);

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-end justify-center px-0 sm:items-center sm:px-4 modal-overlay"
      variants={overlayVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <motion.div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        variants={overlayVariants}
        onClick={onClose}
      />

      <motion.div
        variants={sheetVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="relative z-10 flex w-full flex-col overflow-hidden rounded-t-3xl bg-(--color-paper) shadow-(--shadow-soft) sm:max-w-lg sm:rounded-2xl modal-sheet"
      >
        <div className="sm:hidden flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-(--border-strong) opacity-40" />
        </div>

        <div className="flex items-start justify-between px-6 pt-3 pb-3 sm:pt-6 shrink-0">
          <div>
            <h3
              className="text-xl font-bold text-(--accent)"
              style={{ fontFamily: "ofont_flow" }}
            >
              «Серая Шейка»
            </h3>
            <p className="text-sm text-(--text-muted) mt-0.5">
              а.г. Полыковичи, ул. Центральная 72А
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-(--border-soft) hover:bg-(--border-strong) transition-colors text-(--text-muted) text-lg leading-none cursor-pointer"
            aria-label="Закрыть"
          >
            ✕
          </button>
        </div>

        <div className="px-6 pb-10 flex-1 min-h-0 modal-content">
          <div
            className="h-[clamp(15rem,38dvh,20rem)] touch-none overflow-hidden rounded-xl sm:h-85"
            onWheel={(e) => e.stopPropagation()}
          >
            <YMaps>
              <Map defaultState={defaultState} width="100%" height="100%">
                <Placemark geometry={[53.979, 30.37139]} />
              </Map>
            </YMaps>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
